import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { 
  ArrowRight, 
  Building2, 
  Upload,
  CheckCircle2,
  Loader2
} from 'lucide-react';
import { motion } from 'framer-motion';
import axios from 'axios';

const SubmitProjectPage = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  
  const [formData, setFormData] = useState({
    company_name: '',
    contact_name: '',
    contact_role: '',
    contact_email: '',
    project_name: '',
    asset_type: '',
    location: '',
    project_stage: '',
    capital_sought: '',
    funding_structure: '',
    target_timeline: '',
    has_investor_network: '',
    wants_platform_sourcing: '',
    governance_needs: '',
    pitch_deck_url: '',
    additional_notes: '',
  });

  const assetTypes = [
    { value: 'residential', label: 'Residential' },
    { value: 'commercial', label: 'Commercial' },
    { value: 'mixed_use', label: 'Mixed Use' },
    { value: 'student_housing', label: 'Student Housing' },
    { value: 'hospitality', label: 'Hospitality' },
    { value: 'industrial', label: 'Industrial' },
    { value: 'land', label: 'Land / Development' },
    { value: 'other', label: 'Other' },
  ];

  const projectStages = [
    { value: 'concept', label: 'Concept / Pre-Development' },
    { value: 'planning', label: 'Planning / Permitting' },
    { value: 'construction', label: 'Under Construction' },
    { value: 'stabilized', label: 'Stabilized / Operating' },
    { value: 'value_add', label: 'Value-Add / Renovation' },
  ];

  const fundingStructures = [
    { value: 'equity', label: 'Equity' },
    { value: 'debt', label: 'Debt / Loan' },
    { value: 'hybrid', label: 'Hybrid (Equity + Debt)' },
    { value: 'convertible', label: 'Convertible Note' },
    { value: 'other', label: 'Other / TBD' },
  ];

  const timelineOptions = [
    { value: '1_month', label: 'Within 1 month' },
    { value: '1_3_months', label: '1-3 months' },
    { value: '3_6_months', label: '3-6 months' },
    { value: '6_12_months', label: '6-12 months' },
    { value: 'flexible', label: 'Flexible' },
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.company_name || !formData.contact_name || !formData.contact_email || !formData.project_name) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/projects/submit`, formData);
      setIsSubmitted(true);
      toast.success('Project submitted successfully!');
    } catch (error) {
      console.error('Error submitting project:', error);
      toast.error('Failed to submit project. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen" data-testid="submit-project-page">
        <Header />
        
        <section className="py-20 md:py-32">
          <div className="container mx-auto px-6 md:px-12 lg:px-24 xl:px-32">
            <div className="max-w-2xl mx-auto text-center">
              <div className="h-20 w-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="h-10 w-10 text-green-500" />
              </div>
              <h1 className="text-3xl md:text-4xl font-heading font-bold mb-4">
                Thank You for Your Submission!
              </h1>
              <p className="text-lg text-foreground-muted mb-8">
                We've received your project details and our team will review them shortly. 
                Expect to hear from us within 2-3 business days.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  size="lg"
                  className="bg-primary hover:bg-primary-hover rounded-full px-8"
                  onClick={() => navigate('/for-developers')}
                >
                  Back to For Developers
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="rounded-full px-8"
                  onClick={() => navigate('/projects')}
                >
                  Browse Projects
                </Button>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="submit-project-page">
      <Header />

      {/* Hero Section - matching Projects page design, centered */}
      <section className="bg-white border-b border-black/[0.04]">
        <div className="container mx-auto px-6 md:px-12 lg:px-20 py-12 md:py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto text-center"
          >
            <p className="text-primary font-medium tracking-[0.2em] uppercase text-sm mb-4">
              For Developers
            </p>
            <h1 className="text-4xl md:text-5xl font-heading font-medium tracking-tight mb-4" data-testid="submit-project-heading">
              Submit Your Project
            </h1>
            <p className="text-lg text-foreground-muted">
              Tell us about your project and we'll get back to you within 48h to discuss next steps.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-8 md:py-12 bg-[rgb(var(--background))]">
        <div className="container mx-auto px-6 md:px-12 lg:px-24 xl:px-32">
          <form onSubmit={handleSubmit} className="max-w-3xl mx-auto">
            <div className="bg-background-paper rounded-2xl border border-white/5 p-6 md:p-8 space-y-8">
              
              {/* Section: Sponsor Information */}
              <div>
                <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-primary" />
                  Sponsor Information
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="company_name">Company / Sponsor Name *</Label>
                    <Input
                      id="company_name"
                      name="company_name"
                      value={formData.company_name}
                      onChange={handleInputChange}
                      placeholder="Your company name"
                      required
                      data-testid="input-company-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contact_name">Contact Name *</Label>
                    <Input
                      id="contact_name"
                      name="contact_name"
                      value={formData.contact_name}
                      onChange={handleInputChange}
                      placeholder="Your full name"
                      required
                      data-testid="input-contact-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contact_role">Your Role</Label>
                    <Input
                      id="contact_role"
                      name="contact_role"
                      value={formData.contact_role}
                      onChange={handleInputChange}
                      placeholder="e.g., CEO, Managing Partner"
                      data-testid="input-contact-role"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contact_email">Contact Email *</Label>
                    <Input
                      id="contact_email"
                      name="contact_email"
                      type="email"
                      value={formData.contact_email}
                      onChange={handleInputChange}
                      placeholder="you@company.com"
                      required
                      data-testid="input-contact-email"
                    />
                  </div>
                </div>
              </div>

              {/* Section: Project Details */}
              <div>
                <h2 className="text-xl font-semibold mb-4">Project Details</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="project_name">Project Name *</Label>
                    <Input
                      id="project_name"
                      name="project_name"
                      value={formData.project_name}
                      onChange={handleInputChange}
                      placeholder="Name of the project"
                      required
                      data-testid="input-project-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="asset_type">Asset Type</Label>
                    <Select value={formData.asset_type} onValueChange={(val) => handleSelectChange('asset_type', val)}>
                      <SelectTrigger data-testid="select-asset-type">
                        <SelectValue placeholder="Select asset type" />
                      </SelectTrigger>
                      <SelectContent>
                        {assetTypes.map(type => (
                          <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="location">Location</Label>
                    <Input
                      id="location"
                      name="location"
                      value={formData.location}
                      onChange={handleInputChange}
                      placeholder="City, Country"
                      data-testid="input-location"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="project_stage">Project Stage</Label>
                    <Select value={formData.project_stage} onValueChange={(val) => handleSelectChange('project_stage', val)}>
                      <SelectTrigger data-testid="select-project-stage">
                        <SelectValue placeholder="Select project stage" />
                      </SelectTrigger>
                      <SelectContent>
                        {projectStages.map(stage => (
                          <SelectItem key={stage.value} value={stage.value}>{stage.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Section: Capital & Timeline */}
              <div>
                <h2 className="text-xl font-semibold mb-4">Capital & Timeline</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="capital_sought">Capital Sought</Label>
                    <Input
                      id="capital_sought"
                      name="capital_sought"
                      value={formData.capital_sought}
                      onChange={handleInputChange}
                      placeholder="e.g., €500,000"
                      data-testid="input-capital-sought"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="funding_structure">Funding Structure</Label>
                    <Select value={formData.funding_structure} onValueChange={(val) => handleSelectChange('funding_structure', val)}>
                      <SelectTrigger data-testid="select-funding-structure">
                        <SelectValue placeholder="Select structure" />
                      </SelectTrigger>
                      <SelectContent>
                        {fundingStructures.map(structure => (
                          <SelectItem key={structure.value} value={structure.value}>{structure.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="target_timeline">Target Raise Timeline</Label>
                    <Select value={formData.target_timeline} onValueChange={(val) => handleSelectChange('target_timeline', val)}>
                      <SelectTrigger data-testid="select-target-timeline">
                        <SelectValue placeholder="Select timeline" />
                      </SelectTrigger>
                      <SelectContent>
                        {timelineOptions.map(option => (
                          <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Section: Investor Network */}
              <div>
                <h2 className="text-xl font-semibold mb-4">Investor Network</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="has_investor_network">Do you have an existing investor network?</Label>
                    <Select value={formData.has_investor_network} onValueChange={(val) => handleSelectChange('has_investor_network', val)}>
                      <SelectTrigger data-testid="select-has-network">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="yes">Yes</SelectItem>
                        <SelectItem value="no">No</SelectItem>
                        <SelectItem value="partial">Partially</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="wants_platform_sourcing">Would you like Flippex to help source investors?</Label>
                    <Select value={formData.wants_platform_sourcing} onValueChange={(val) => handleSelectChange('wants_platform_sourcing', val)}>
                      <SelectTrigger data-testid="select-wants-sourcing">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="yes">Yes</SelectItem>
                        <SelectItem value="no">No</SelectItem>
                        <SelectItem value="maybe">Maybe / TBD</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Section: Additional Info */}
              <div>
                <h2 className="text-xl font-semibold mb-4">Additional Information</h2>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="governance_needs">Governance / Reporting Needs</Label>
                    <Textarea
                      id="governance_needs"
                      name="governance_needs"
                      value={formData.governance_needs}
                      onChange={handleInputChange}
                      placeholder="Describe any specific governance or reporting requirements..."
                      rows={3}
                      data-testid="textarea-governance"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="pitch_deck_url">Pitch Deck / Teaser URL</Label>
                    <Input
                      id="pitch_deck_url"
                      name="pitch_deck_url"
                      value={formData.pitch_deck_url}
                      onChange={handleInputChange}
                      placeholder="Link to your pitch deck (Google Drive, Dropbox, etc.)"
                      data-testid="input-pitch-deck"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="additional_notes">Additional Notes</Label>
                    <Textarea
                      id="additional_notes"
                      name="additional_notes"
                      value={formData.additional_notes}
                      onChange={handleInputChange}
                      placeholder="Any other information you'd like to share..."
                      rows={3}
                      data-testid="textarea-notes"
                    />
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <div className="pt-4">
                <Button
                  type="submit"
                  size="lg"
                  className="w-full bg-primary hover:bg-primary-hover rounded-full"
                  disabled={isSubmitting}
                  data-testid="submit-project-btn"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      Submit Project <ArrowRight className="ml-2 h-5 w-5" />
                    </>
                  )}
                </Button>
                <p className="text-xs text-foreground-muted text-center mt-4">
                  By submitting, you agree to our Terms of Service and Privacy Policy.
                </p>
              </div>
            </div>
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SubmitProjectPage;
